<template>
    <div aria-hidden="true" class="orb" :style="{ width: `${size}px`, height: `${size}px` }">
        <!-- Spin and pulse are separate elements because they are both transforms:
             on one element the second animation would simply replace the first. -->
        <div class="orb-spin" :class="{ 'is-spinning': spinning }">
            <div class="orb-body" :class="{ 'is-still': !animated }" :style="{ filter: `blur(${blurRadius}px)` }"></div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

const {
    size = 56,
    animated = true,
    spinning = false,
} = defineProps<{
    size?: number;
    animated?: boolean;
    /**
     * Rotate continuously, for the loading screens. The orb is a circle, so the
     * off-centre highlight in its gradient is the only thing that reads as
     * motion -- which is exactly what makes a sweep say "working" rather than
     * "breathing", as the idle pulse alone does.
     */
    spinning?: boolean;
}>();

const blurRadius = computed(() => Math.max(5, size * 0.13));
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .orb {
        @apply shrink-0;
    }

    .orb-spin {
        @apply h-full w-full;
    }

    .orb-spin.is-spinning {
        animation: orb-spin 2.8s linear infinite;
    }

    .orb-body {
        @apply h-full w-full rounded-full;
        background: radial-gradient(
            60% 55% at 42% 30%,
            var(--color-valence-pink-600) 0%,
            var(--color-valence-pink-500) 38%,
            var(--color-valence-gold-600) 72%,
            var(--color-valence-gold-400) 100%
        );
        animation: orb-pulse 6s ease-in-out infinite;
    }

    .orb-body.is-still {
        animation: none;
    }
}

@keyframes orb-spin {
    to {
        transform: rotate(360deg);
    }
}

@keyframes orb-pulse {
    0%,
    100% {
        transform: scale(1) rotate(0deg);
    }
    50% {
        transform: scale(1.08) rotate(8deg);
    }
}

@media (prefers-reduced-motion: reduce) {
    .orb-body,
    .orb-spin.is-spinning {
        animation: none;
    }
}
</style>
